<script setup lang="ts">
import {IProgress, JavascriptViewer} from "@3dweb/360javascriptviewer";
import {getViewerFallbackState} from '@/composables/viewerFallback';
import {homepageViewerImageConfig} from '@/composables/homepageViewerAssets';
import {reportHomepageViewerError} from '@/composables/homepageViewerDiagnostics';
import type {HomepageViewerPhase} from '@/composables/homepageViewerDiagnostics';


const jsv = ref<JavascriptViewer>();
const showLoader = ref(true);
const captionText = ref('Checking for more images to create a 360 view');
const viewerUnavailable = ref(false);

const mainImageUrl = ref(homepageViewerImageConfig.initialImageUrl);
let disposed = false;
let mountTimer: ReturnType<typeof setTimeout> | undefined;
let loopFrame: number | undefined;
let viewerPhase: HomepageViewerPhase = 'startup';
let loadedImages = 0;

const handleViewerError = (err: unknown) => {
  if (disposed) return;

  const error = err instanceof Error ? err : new Error(String(err));
  const image = document.getElementById('jsv-image-viewer');
  reportHomepageViewerError(error, {
    phase: viewerPhase,
    loadedImages,
    online: navigator.onLine,
    initialImageReady: image instanceof HTMLImageElement && image.complete && image.naturalWidth >= 10 && image.naturalHeight >= 10,
    pageHidden: document.hidden,
  });
  console.error('Error in viewer:', error);
  const fallback = getViewerFallbackState(err);
  viewerUnavailable.value = fallback.unavailable;
  showLoader.value = true;
  captionText.value = fallback.message;
  loopAble.value = false;
};

const loadAnimation = async () => {
  if (disposed || !document.getElementById('jsv-holder-viewer') || !document.getElementById('jsv-image-viewer')) {
    return;
  }

  showLoader.value = false;
  jsv.value = new JavascriptViewer({
    mainHolderId: 'jsv-holder-viewer',
    mainImageUrl: mainImageUrl.value,
    mainImageId: 'jsv-image-viewer',
    totalFrames: 72,
    imageUrlFormat: homepageViewerImageConfig.imageUrlFormat,
    license: '4yaodqiquhfbudoemhmviii6',
    defaultProgressBar: true,
    zoom: true,
    inertia: 12,
    speed: 90,
    autoCDNResizer: homepageViewerImageConfig.autoCDNResizer,
    zoomMax: 2,
    enableChangeImageEvent: true,
    notificationConfig: {
      dragToRotate: {
        showStartToRotateDefaultNotification: true
      }
    }
  });

  jsv.value.events().loadImage.on((progress: IProgress) => {
    loadedImages = progress.currentImage;
    captionText.value = `Loading image ${progress.currentImage} from ${progress.totalImages} <span class="text-secondary">(${progress.percentage}%)</span>`
  });

  jsv.value.events().startDragging.on((ready) => {
    setTimer(10);
  });

  jsv.value.events().scroll.on((ready) => {
    setTimer(10);
  });

  jsv.value.events().pinch.on((ready) => {
    setTimer(10);
  });

  jsv.value.events().changeImage.on((status) => {
    currentImageSequence.value = status.currentImage.sequence;
  });


  await jsv.value.start();
  if (disposed || !jsv.value) return;

  showLoader.value = false;
  viewerPhase = 'intro-rotation';
  await jsv.value.rotateToDegree(180, false);
  if (disposed || !jsv.value) return;

  await jsv.value.rotateToDegree(90);
  if (disposed) return;

  viewerPhase = 'interactive';
  setTimer(5);
  captionText.value = "Now you can rotate this image and zoom with scroll or pinch";
};

const timer = ref();
const setTimer = (seconds: number) => {
  nTimes.value = 0;
  if (jsv.value !== undefined) {
    jsv.value.setSpeed(90);
    clearTimeout(timer.value);
    loopAble.value = false;
    timer.value = setTimeout(() => {
      loopAble.value = true;
      loop();
    }, seconds * 1000);
  }
};

const currentImageSequence = ref(0);
const loopAble = ref(true);
const nTimes = ref(0);
const loop = (degrees = 5, start = true) => {
  if (jsv.value !== undefined) {
    const outsideRange = currentImageSequence.value > 32 || currentImageSequence.value < 8;
    if (loopAble.value) {
      if (start && (jsv.value.isZoomedIn() || outsideRange)) {
        jsv.value.resetZoom().then(() => {
          if (jsv.value !== undefined) {
            jsv.value.rotateToFrame(21).then(() => {
              loopFrame = requestAnimationFrame(() => loop(degrees));
            }).catch(handleViewerError);
          }
        }).catch(handleViewerError)
      } else {
        jsv.value.setSpeed(2)
        nTimes.value++;
        if (outsideRange) {
          degrees *= -1;
        }
        jsv.value.rotateDegrees(degrees).then(() => {
          if (nTimes.value < 90) {
            loopFrame = requestAnimationFrame(() => loop(degrees, false));
          }
        }).catch(handleViewerError);
      }
    }
  }
};


onMounted(() => {
  const timoutDelay = 500;
  disposed = false;
  showLoader.value = true;
  viewerUnavailable.value = false;
  mountTimer = setTimeout(() => {
    void loadAnimation().catch(handleViewerError);
  }, timoutDelay);
})

onBeforeUnmount(() => {
  disposed = true;
  loopAble.value = false;
  clearTimeout(mountTimer);
  clearTimeout(timer.value);
  if (loopFrame !== undefined) {
    cancelAnimationFrame(loopFrame);
  }

  const viewer = jsv.value;
  jsv.value = undefined;
  if (viewer) {
    void viewer.destroy().catch(() => undefined);
  }
})

</script>

<template>
  <div>
    <div class="presentation flex justify-content-center">
      <div id="jsv-holder-viewer">
        <img
          id="jsv-image-viewer"
          alt="Gold smartphone in the interactive 360° product viewer"
          width="222"
          height="320"
          v-bind:src="mainImageUrl"
        />
      </div>
      <p v-if="viewerUnavailable" class="viewer-fallback" role="status">
        {{ captionText }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.presentation {
  align-items: center;
}

@media (max-width: 576px) {
  #jsv-holder-viewer {
    width: 100%;
  }
}

@media (min-width: 768px) {
  .presentation {
    min-height: 26rem;
  }

  #jsv-holder-viewer {
    height: 320px;
  }
}

#jsv-holder-viewer {
  width: 310px;
  z-index: 500;
  display: flex;
  justify-content: center;

  &:deep(>div) {
    overflow: unset !important;
  }
}

.viewer-fallback {
  margin: 0.75rem 1rem 0;
  text-align: center;
}

#jsv-holder-viewer div:nth-child(3) {
  overflow: unset !important;
}
</style>
